<template>
  <div class="col-lg-6 col-md-8 mx-auto my-5">
    <div class="d-flex justify-content-between">
      <icon :icon="`a-gradient`" :width="40" :height="40" class="al-logo-main mb-4"></icon>
      <div class="get-started t-primary t-sm">
        Already have an account? <router-link :to="{ name: 'login' }" class="btn btn-secondary--grey ml-2 t-700 t-uppercase t-xs">Log in</router-link>
      </div>
    </div>

    <h2 class="t-primary t-600 mb-0">
      Create your new account.
    </h2>
    <p class="mt-2 mb-0 t-regular">
      If you already have an account, <router-link :to="{ name: 'login' }">log in here</router-link>.<br />
      Didn't receive your verification email or can't find it? <router-link :to="{ name: 'confirmation.resend' }">Resend it</router-link>.
    </p>

    <hr class="hr-small mb-4" />

    <!-- render static messages -->
    <static-messages v-if="error" :messages="error" :variant="`danger`" @closed="error = null"/>

    <!-- register with email -->
    <form @submit.prevent="validateBeforeSubmit" autocomplete="off">
      <!-- <h2 class=" t-md mb-3">
        Welcome<span v-if="firstname">, {{ firstname }} </span>
      </h2> -->
      <!-- email -->
      <div class="form-group mb-3">
        <label for="email" class="t-500 t-sm">Email</label>
        <input ref="email"
          type="text"
          class="form-control form-control-lg"
          name="email"
          v-model="email"
          v-validate.disable="'required|email'"
          data-vv-validate-on="blur"
          data-vv-delay="1000"
          data-vv-as="email"
          placeholder="Enter your professional email"
          :class="{'is-invalid': errors.has('email') }"
          @focus="showTooltip = true"
          @blur="fillInUser()"
        />

        <custom-tooltip v-if="showTooltip">
          <p slot="tooltip-text" class="mb-0 t-sm t-regular block-fa-span clearfix">
            <i class="fa fa-warning pr-2 t-yellow" />
            <span class="block-span">Please use your <span class="t-700">professional email</span>. It enables you to have a better experience on AcademicLabs and helps us maintain quality of our platform.</span>
          </p>
        </custom-tooltip>
        <span class="invalid-feedback" v-show="errors.has('email')">{{ errors.first('email') }}</span>
      </div>

      <!-- first name -->
      <div class="form-group mb-3">
        <label for="firstname" class="t-500 t-sm">First name</label>
        <input type="text"
          class="form-control form-control-lg"
          placeholder="First name"
          name="firstname"
          autocomplete="firstname"
          v-model="firstname"
          v-validate="'required'"
          data-vv-validate-on="blur"
          data-vv-as="first name"
          :class="{'is-invalid': errors.has('firstname') }"
        />
        <span class="invalid-feedback" v-show="errors.has('firstname')">{{ errors.first('firstname') }}</span>
      </div>

      <!-- last name -->
      <div class="form-group mb-3">
        <label for="lastname" class="t-500 t-sm">Last name</label>
        <input type="text"
          class="form-control form-control-lg"
          placeholder="Last name"
          autocomplete="lastname"
          name="lastname"
          v-model="lastname"
          v-validate="'required'"
          data-vv-validate-on="blur"
          data-vv-as="last name"
          :class="{'is-invalid': errors.has('lastname') }"
        />
        <span class="invalid-feedback" v-show="errors.has('lastname')">{{ errors.first('lastname') }}</span>
      </div>

      <!-- password -->
      <div class="form-group mb-3">
        <label for="password" class="t-500 t-sm">Password</label>
        <input
          type="password"
          placeholder="Password"
          autocomplete="new-password"
          name="password"
          v-model="password"
          v-validate="'required|min:8'"
          data-vv-validate-on="blur"
          class="form-control form-control-lg"
          :class="{'is-invalid': errors.has('password') }"
          @focus="togglePasswordTooltip()"
          @blur="togglePasswordTooltip()"
          @input="togglePasswordTooltip()"
        />

        <custom-tooltip v-if="isPasswordTooltip">
          <p slot="tooltip-text" class="mb-0 t-sm t-regular block-fa-span clearfix">
            <i class="fa fa-warning t-yellow pr-2" />
            <span class="block-span">For your own security, we recommend you to enter a password with minimum <strong>8</strong> characters.</span>
          </p>
        </custom-tooltip>

        <span class="invalid-feedback" v-show="errors.has('password')">{{ errors.first('password') }}</span>
      </div>

      <label for="couponCode" class="t-500 t-sm width--100per text-left">Coupon code (optional)</label>
      <div class="form-inline mb-4">
        <input
          type="text"
          class="form-control form-control-lg flex-fill"
          placeholder="Enter here and click Validate"
          name="couponCode"
          v-model="couponCode"
          :class="{'is-invalid': errors.has('coupon') }"
          @keypress.enter.prevent="validateCoupon"
        />
        <button
          class="btn btn-primary ml-2"
          type="button"
          @click="validateCoupon"
          :disabled="isValidatingCoupon || !couponCode"
          :class="{
            'btn-primary--green': validatedCoupon
          }"
        >{{ validatedCoupon ? 'Validated' : 'Validate' }}</button>
        <span class="invalid-feedback" v-show="errors.has('coupon')">{{ errors.first('coupon') }}</span>
        <div class="width--100per mt-1" v-if="validatedCoupon">
          <i class="fa fa-check t-green mr-2 t-sm"></i>
          <span class="t-xs">{{ couponTimeRemaining }}</span>
        </div>
      </div>

      <div class="action-btn mt-4 clearfix">
        <form-buttons :isRequest="isRequest"
          :isCancel="false"
          :createText="buttonText"
          :btnClass="`width--100per`"
          :containerClass="`bd-none`">
        </form-buttons>

        <p class="font-italic t-xs mb-0 mt-4">
          <i class="fa fa-info-circle pr-1 t-secondary"></i>
          By proceeding, you agree to the AcademicLabs <a href="https://www.academiclabs.com/terms" title="Terms" alt="terms" target="_blank">Terms</a>, <a href="https://www.academiclabs.com/privacy-policy" title="Privacy policy" alt="privacy-policy" target="_blank">Privacy policy</a> and <a href="https://www.academiclabs.com/cookie-policy" title="Cookie policy" alt="cookie-policy" target="_blank">Cookie policy</a>.
        </p>
      </div>
    </form>
  </div>
</template>

<script>
import AuthService from '@/services/auth'
import UserService from '@/services/user'
import SharedService from '@/services/shared'

import StaticMessages from '@/components/shared/StaticMessages'
import Icon from '@/components/shared/Icon'
import CustomTooltip from '@/components/custom_tooltip/CustomTooltip'
import FormButtons from '@/components/shared/form/Buttons'

export default {
  name: 'signUpRegister',
  components: {
    StaticMessages,
    Icon,
    CustomTooltip,
    FormButtons
  },
  data () {
    return {
      email: '',
      firstname: null,
      lastname: null,
      password: '',
      couponCode: '',
      validatedCoupon: null,
      isValidatingCoupon: false,
      error: null,
      isRequest: false,
      showTooltip: false,
      isPasswordTooltip: false
    }
  },
  mounted () {
    this.couponCode = this.$route.query?.coupon || ''
    this.email = this.$route.query?.email || ''
    if (this.email) this.fillInUser()
  },
  computed: {
    buttonText () {
      let text = `Let's get started`
      if (this.firstname) text += `, ${this.$options.filters.capitalize(this.firstname)}`
      text += '!'
      return text
    },
    couponTimeRemaining () {
      if (!this.validatedCoupon) return null
      if (this.validatedCoupon.days_remaining > 0) {
        return `${this.validatedCoupon.days_remaining} days of ${this.validatedCoupon.type} access remaining`
      }
      return `You will get ${this.validatedCoupon.type} access for this day`
    }
  },
  methods: {
    validateCoupon () {
      // Don't do anything if coupon is being validated or no coupon is entered
      if (!this.couponCode || this.isValidatingCoupon) return
      // Make sure user has entered email address first
      if (!this.email) {
        this.errors.add({
          field: 'coupon',
          msg: 'Please enter your email address first'
        })
        return
      }
      // Reset state
      this.validatedCoupon = null
      this.isValidatingCoupon = true
      this.errors.remove('coupon')

      UserService.validateCoupon(this.email, this.couponCode).then(validatedCoupon => {
        this.validatedCoupon = validatedCoupon
        this.isValidatingCoupon = false
      }).catch(err => {
        if (err.data) {
          this.errors.add({
            field: 'coupon',
            msg: err.data
          })
        } else {
          this.error = this.handleError(err)
        }
        this.isValidatingCoupon = false
      })
    },
    togglePasswordTooltip () {
      this.password.length < 8 ? this.isPasswordTooltip = true : this.isPasswordTooltip = false
    },
    fillInUser () {
      this.$validator.validate('email', this.email).then(result => {
        if (!result) return

        if (result && !this.firstname && !this.lastname && this.email) {
          this.email = this.email.toLowerCase()
          let name = this.getNameFromEmail(this.email)
          this.firstname = name.firstName
          this.lastname = name.lastName
          this.showTooltip = false
        }

        SharedService.domains(this.email).then(domain => {
          // Show tooltip if user enters public email domain
          this.showTooltip = domain.public_domain
        }).catch(err => {
          this.error = this.handleError(err)
          this.isRequest = false
        })
      })
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result && !this.isRequest) {
          this.isRequest = true

          this.$auth.trackRegistration(this.email)

          let credentials = {
            email: this.email,
            last_name: this.$options.filters.capitalize(this.lastname),
            first_name: this.$options.filters.capitalize(this.firstname),
            password: this.password,
            coupon_code: this.couponCode,
            mixpanel_distinct_id: this.$mp.getDistinctID(),
            category_id: 4
          }

          AuthService.register(credentials).then(register => {
            let registerData = this.$ls.getRegisterData()
            registerData.email = credentials.email
            registerData.name = credentials.first_name

            this.$auth.trackRegistrationSuccess(this.email)

            this.$ls.setRegisterData(registerData)
            this.isRequest = false
            this.$router.push({ name: 'confirmation.pending' })
          }).catch(err => {
            this.error = this.handleError(err)
            this.isRequest = false
          })
        }
      })
    }
  }
}
</script>
